import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Checkbox, styled } from '@mui/material';
import classnames from 'classnames';
import {
  SCHEMA_DISPLAY_NUM_SAMPLES_DEFAULT,
  SchemaBrowserType,
  SchemaEditorSampleError,
  SchemaEditorViewModeType,
} from 'js/constants/schemaEditor';
import { getSampleValue } from 'js/lib/RecipeBuilderUtils';
import { compact, filter, forEach, isEmpty, map } from 'lodash';
import { ReactComponent as GlobeIcon } from 'new/components/icons/fi-rr-globe-alt.svg';
import { ReactComponent as UsersIcon } from 'new/components/icons/fi-rr-users.svg';
import { ReactComponent as InfoIconFilled } from 'new/components/icons/fi-sr-info.svg';
import { ReactComponent as LockIcon } from 'new/components/icons/lock.svg';
import React from 'react';
import { connect } from 'react-redux';
import Tooltip from '../elements/mui/Tooltip';
import ResizableGrid from '../elements/ResizableGrid';
import MissingValueIcon from '../elements/schema-editor/icons/MissingValueIcon';
import SchemaBrowserSamples from '../elements/schema-editor/schema-browser/SchemaBrowserSamples';
import AttributeInfoDrawer from './AttributeInfoDrawer';
import MetadataToggle from './MetadataToggle';
import RecipeBuilderAttributeTypes from './RecipeBuilderAttributeTypes';
import { FLOW_TYPE_API_SERVER } from 'js/constants/pipelineConstants';
import { deselectRecipeBuilderAttr, selectRecipeBuilderAttr } from 'js/actions';
import nexsetTypes from 'new/constants/nexsetTypes';

const muiStyles = {
  checkbox: {
    root: {
      width: 24,
      fontSize: 20,
    },
  },
};

const StyledCheckbox = styled(Checkbox)(muiStyles.checkbox);

const sensitivityMap = {
  Confidential: {
    label: 'Confidential',
    icon: <LockIcon />,
  },
  Internal: {
    label: 'Internal',
    icon: <UsersIcon />,
  },
  Public: {
    label: 'Public',
    icon: <GlobeIcon />,
  },
};

class RecipeBuilderGrid extends React.PureComponent {
  constructor(props) {
    super(props);

    const filteredState = this._getFilteredStateFromProps(props);

    this.state = {
      ...filteredState,
      infoRowIndex: -1,
      gridWidth: 0,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      selectedViewMode,
      schema,
      metaSchema,
      showMeta,
      selectedAttrs,
      samplesUpdatedAt,
      selectedSplitOutputId,
      isLoading,
      attributeSelectionDisabled,
      searchText,
    } = this.props;

    if (
      this._grid &&
      selectedViewMode === SchemaEditorViewModeType.TABLE &&
      (schema !== prevProps.schema ||
        selectedAttrs !== prevProps.selectedAttrs ||
        samplesUpdatedAt !== prevProps.samplesUpdatedAt ||
        selectedSplitOutputId !== prevProps.selectedSplitOutputId ||
        (isLoading && !prevProps.isLoading))
    ) {
      this._grid.recomputeGridSize();
      this._grid.forceUpdate();
    }

    if (this._grid && attributeSelectionDisabled !== prevProps.attributeSelectionDisabled) {
      this._grid.recomputeGridSize();
    }

    if (
      schema !== prevProps.schema ||
      metaSchema !== prevProps.metaSchema ||
      showMeta !== prevProps.showMeta
    ) {
      const newState = this._getFilteredStateFromProps(this.props);
      this.setState(newState);
    }

    if (searchText !== prevProps.searchText) {
      this._showSearchResults(this._getEffectiveSchema(this.props), this.props.searchText);
    }

    if (this._grid && this._grid._bottomRightGrid) {
      const newGridWidth = this._grid._bottomRightGrid.getTotalColumnsWidth();
      if (newGridWidth !== this.state.gridWidth) {
        this.setState({
          gridWidth: this._grid._bottomRightGrid.getTotalColumnsWidth(),
        });
      }
    }
  }

  handleGridRefInitialized = (g, resizableGrid) => {
    this._grid = g;
    this._resizableGrid = resizableGrid;

    /**
     * If the grid width is 0, set the grid width to the last rendered width
     * This is to ensure that the grid width is set to the correct width when the grid is first rendered
     */
    if (this.state.gridWidth === 0 && g?._lastRenderedWidth) {
      this.setState({ gridWidth: g?._lastRenderedWidth });
    }
  };

  render() {
    const {
      gridId,
      dataSet,
      nestedSamples,
      selectedViewMode,
      customSamplesCode,
      showSamples,
      allowEditing,
      liveSamples,
      showMeta,
      showCharacteristics,
      selectedSplitOutputId,
      isLoading,
      onResetClicked,
      onSaveClicked,
      onEdit,
      hideExpandMetaToggle,
      darkEditor,
    } = this.props;
    const { infoRowIndex, gridWidth, filteredSchema } = this.state;

    const numSampleColumns = showSamples
      ? Math.max(SCHEMA_DISPLAY_NUM_SAMPLES_DEFAULT, this._getNumSamples())
      : 0;
    const columnCount = numSampleColumns + 2;
    const isInfoOpen = infoRowIndex > -1;
    const codeSamples =
      selectedViewMode === SchemaEditorViewModeType.CODE && selectedSplitOutputId
        ? filter(nestedSamples, ({ forkToDataset }) => forkToDataset == selectedSplitOutputId)
        : nestedSamples;

    // API server flow nodes can be identified by:
    // 1. flow_type === FLOW_TYPE_API_SERVER
    // 2. node_type being an API request node type (api_request, api_result, api_error, api_success_modifier)
    // 3. nexset_type === 'api_endpoint'
    const isApiServerFlowNode =
      dataSet?.flow_type === FLOW_TYPE_API_SERVER ||
      [
        'api_request',
        'api_result',
        'api_error',
        'api_success_modifier',
        'splitter_output',
      ].includes(dataSet?.node_type) ||
      dataSet?.nexset_type === nexsetTypes.api_endpoint;

    // API server flows can edit samples for testing, but cannot save to cache
    const allowCacheSave = allowEditing && !isApiServerFlowNode;

    return (
      <>
        {selectedViewMode === SchemaEditorViewModeType.TABLE && !isEmpty(filteredSchema) && (
          <div className="recipe-samples-grid-conatiner">
            <MetadataToggle
              showMeta={showMeta}
              gridWidth={gridWidth}
              disabled={hideExpandMetaToggle}
              onShowMetaChanged={this._handleShowMetaChanged}
            />

            <ResizableGrid
              onRef={this.handleGridRefInitialized}
              onColumnResize={(width) => this.setState({ gridWidth: width })}
              className="recipe-samples-grid"
              rowCount={filteredSchema.length}
              getDefaultRowHeight={() => 48}
              overscanRowCount={5}
              columnCount={columnCount}
              getDefaultColumnWidth={this._getDefaultColumnWidth}
              overscanColumnCount={2}
              getAllowColumnResize={this._getAllowColumnResize}
              getAllowRowResize={this._getAllowRowResize}
              getRowId={this._getRowId}
              getCellContent={this._renderCellContent}
            />
          </div>
        )}

        {selectedViewMode === SchemaEditorViewModeType.CODE && (
          <SchemaBrowserSamples
            samples={codeSamples}
            customSamplesCode={customSamplesCode}
            isEditable={allowEditing}
            allowCacheSave={allowCacheSave}
            isLoading={isLoading}
            showMeta={showMeta}
            liveSamples={liveSamples}
            onResetClicked={onResetClicked}
            onSaveClicked={onSaveClicked}
            onEdit={onEdit}
            onViewModeChanged={this.props.onViewModeChanged}
            resource={this.props?.resource ?? null}
            resourceType={this.props?.resourceType ?? null}
            darkEditor={darkEditor}
          />
        )}

        <AttributeInfoDrawer
          gridId={gridId}
          dataSet={dataSet}
          attr={isInfoOpen ? this._getDatum(infoRowIndex) : null}
          samples={this._getEffectiveSamples()}
          showCharacteristics={showCharacteristics}
          isOpen={isInfoOpen}
          onClose={this._handleCloseInfo}
        />
      </>
    );
  }

  _renderCellContent = (cell) => {
    const { infoRowIndex } = this.state;
    const { rowIndex, columnIndex } = cell;
    let content;

    switch (columnIndex) {
      case 0:
        content = this._renderSelectorCell(cell);
        break;

      case 1:
        content = this._renderAttributeInfoCell(cell);
        break;

      default:
        content = this._renderSamplesCell(cell);
    }

    return (
      <div
        className={classnames('recipe-builder-grid-cell-container', {
          'is-cell-info-open': rowIndex === infoRowIndex,
          'is-metadata-row': this._getDatum(rowIndex)?.__isMetaData,
        })}
      >
        {content}
      </div>
    );
  };

  _renderSelectorCell = ({ rowIndex }) => {
    const { gridId, allowSelection, selectedAttrs } = this.props;
    const rowData = this._getDatum(rowIndex);
    const attrName = rowData.__name;

    // don't show checkboxes for metadata or output grid
    const showCheckbox =
      allowSelection && !(rowData.__isMetaData && gridId === SchemaBrowserType.OUTPUT);

    return (
      <div className="recipe-builder-grid-cell selector-cell">
        {showCheckbox && (
          <StyledCheckbox
            checked={!!(selectedAttrs && selectedAttrs[attrName])}
            onChange={(event, isChecked) => this._handleRowSelected(attrName, isChecked)}
          />
        )}
      </div>
    );
  };

  _renderAttributeInfoCell = ({ rowIndex }) => {
    const { showAttributeInfo, selectedAttrs, semanticSchema } = this.props;
    const { attrNameToIsExpandedMap, attrNameToHasChildrenMap } = this.state;
    const rowData = this._getDatum(rowIndex);
    const { __name } = rowData;
    const isExpanded = attrNameToIsExpandedMap[__name];
    const hasChildren = attrNameToHasChildrenMap[__name];
    const isSelected = selectedAttrs && selectedAttrs[__name];
    const depth = rowData.__depth || 0;
    const className = classnames('recipe-builder-grid-cell attribute-info-cell', {
      controlledCell: rowData.__controlledBy,
      'is-selected': isSelected,
    });
    const ExpandCollapseIcon = isExpanded ? KeyboardArrowDownIcon : KeyboardArrowRightIcon;
    const semanticInfo = this._getSemanticInfo(__name, semanticSchema);

    return (
      <div
        className={className}
        style={{
          paddingLeft: 28 + depth * 20,
        }}
      >
        {showAttributeInfo && (
          <Tooltip title="Samples and Metrics" arrow placement="right">
            <InfoIconFilled
              className="info-icon"
              onClick={() => this._handleInfoClicked(rowIndex)}
            />
          </Tooltip>
        )}
        {hasChildren && (
          <ExpandCollapseIcon
            data-row-index={rowIndex}
            className="expand-collapse-button"
            style={{
              top: 10,
              left: 12 + depth * 20,
            }}
            onClick={this._handleExpandCollapseButtonClick}
          />
        )}
        <div>
          <div className="attr-name">{rowData.__displayName}</div>
          <RecipeBuilderAttributeTypes attr={rowData} semanticInfo={semanticInfo} limit={2} />
        </div>
      </div>
    );
  };

  _renderSamplesCell = ({ rowIndex, columnIndex }) => {
    const rowData = this._getDatum(rowIndex);
    const {
      value: sampleValue,
      error: sampleValueError,
      transformError,
    } = getSampleValue(this._getEffectiveSamples(), rowData, columnIndex - 2);
    let className = classnames('recipe-builder-grid-cell sample-cell', {
      'invalid-cell-value': transformError,
    });
    let sampleContent = <div className="sample" />;

    // if samples have been loaded
    if (sampleValueError !== SchemaEditorSampleError.NOT_LOADED) {
      switch (sampleValueError) {
        case SchemaEditorSampleError.MISSING_VALUE:
          className = classnames(className, 'missing-value');
          sampleContent = <MissingValueIcon />;
          break;

        case SchemaEditorSampleError.FILTERED_OUT:
        case SchemaEditorSampleError.FILLER_CELL:
          className = classnames(className, 'missing-sample');
          break;

        default:
          sampleContent = (
            <div className="sample">
              {sampleValue}
              <br />
            </div>
          );
      }

      if (
        sampleValueError === SchemaEditorSampleError.NULL_VALUE ||
        sampleValueError === SchemaEditorSampleError.UNDEFINED_VALUE
      ) {
        className = classnames(className, 'null-value');
      }
    }

    return (
      <div className={className}>
        {sampleContent}
        {rowIndex === 0 && transformError && (
          <div className="sample-validation-error">
            <Tooltip title={this._getTransformErrorMessage(transformError)} arrow={true}>
              <WarningIcon fontSize="large" style={{ width: 20, color: 'rgb(253, 190, 37)' }} />
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  _getDefaultColumnWidth = (columnIndex) => {
    switch (columnIndex) {
      case 0:
        const { allowSelection, showAttributeInfo } = this.props;

        return allowSelection && showAttributeInfo
          ? 32
          : showAttributeInfo
            ? 4 // only for left padding
            : 0;

      case 1:
        return 200;

      default:
        // if the record in this column is missing
        if (this._getColumnAttrs(columnIndex) === null) {
          return 20;
        }

        return 130;
    }
  };

  _getAllowColumnResize = ({ columnIndex }) => {
    return columnIndex > 0 && this._getColumnAttrs(columnIndex) !== null;
  };

  _getAllowRowResize = () => {
    return true;
  };

  _getNumSamples = () => {
    const samples = this._getEffectiveSamples();

    return isEmpty(samples) ? 0 : samples.length;
  };

  _getFilteredStateFromProps = (props) => {
    const { searchText } = props;
    const attrNameToIsExpandedMap = {};
    const attrNameToHasChildrenMap = {};
    const attrNameToIndexMap = {};
    const attrNameToContainsSearchMatchMap = {}; // whether an attr is the ancestor of a search result
    const effectiveSchema = this._getEffectiveSchema(props);

    forEach(effectiveSchema, (row, index) => {
      attrNameToIndexMap[row.__name] = index;

      if (row.__parentName && !attrNameToHasChildrenMap[row.__parentName]) {
        attrNameToIsExpandedMap[row.__parentName] = true;
        attrNameToHasChildrenMap[row.__parentName] = true;
      }
    });

    const filteredSchema = this._getFilteredSchema(
      effectiveSchema,
      searchText,
      attrNameToIndexMap,
      attrNameToIsExpandedMap,
      attrNameToContainsSearchMatchMap,
    );

    return {
      filteredSchema,
      attrNameToIndexMap,
      attrNameToIsExpandedMap,
      attrNameToHasChildrenMap,
      attrNameToContainsSearchMatchMap,
    };
  };

  _getFilteredSchema = (
    schema,
    searchText,
    attrNameToIndexMap,
    attrNameToIsExpandedMap,
    attrNameToContainsSearchMatchMap,
  ) => {
    // filter out children of collapsed rows
    const expandedRows = filter(schema, (row) => {
      // show this row if it is the root
      if (!row.__parentName) {
        return true;
      }

      // hide this row if one of its ancestors is collapsed
      for (
        let currentRow = row;
        currentRow;
        currentRow = currentRow.__parentName
          ? schema[attrNameToIndexMap[currentRow.__parentName]]
          : undefined
      ) {
        if (currentRow.__parentName && !attrNameToIsExpandedMap[currentRow.__parentName]) {
          return false;
        }
      }

      return true;
    });

    // filter out rows which don't match the search term
    const searchMatches = filter(expandedRows, (row) => {
      // show the matching row and all its ancestors
      return (
        this._rowMatchesSearchText(row, searchText) || attrNameToContainsSearchMatchMap[row.__name]
      );
    });

    return searchMatches;
  };

  /**
   * Retrieves semantic information for a given attribute name from a semantic schema.
   *
   * General logic:
   * - The attribute name (attrName) is split into path parts (e.g., "addresses.[*].phone" => ["addresses", "[*]", "phone"]).
   * - The function traverses the semanticSchema object step by step, following the path parts.
   * - For each part:
   *   - If the part is "[*]", it means the current node is an array, so we delegate to _traverseArrayItems to get the schema for array items.
   *     - _traverseArrayItems handles:
   *       1. Standard JSON Schema arrays (with "items" property): returns the schema for items.
   *       2. Legacy array format (array of schemas): returns the first element.
   *       3. Malformed schemas (type "array" but no "items"): returns the schema itself.
   *       4. Otherwise, returns null.
   *   - Otherwise, we navigate to the field by key.
   *     - If the resulting node has a "properties" object (and not "semantic_attributes"), we assume it's a nested object and continue with its properties.
   * - After traversing all path parts, we check the final node:
   *   - If it has "semantic_attributes" (new format), return that.
   *   - Otherwise, if it has "semantic_type" (legacy format), return the node itself.
   *   - If neither, return null.
   *
   * This logic ensures compatibility with both modern JSON Schema and legacy Nexla schema formats, and handles arrays, nested objects, and union types robustly.
   */
  _getSemanticInfo = (attrName, semanticSchema) => {
    if (!semanticSchema && !attrName) return null;
    const pathParts = attrName.split('.');

    let current = semanticSchema;
    for (const part of pathParts) {
      if (!current || typeof current !== 'object') return null;

      // If the path part is "[*]", we are traversing into an array.
      // _traverseArrayItems will handle different array schema representations.
      if (part === '[*]') {
        current = this._traverseArrayItems(current);
        if (!current) return null;
      } else {
        // Otherwise, navigate to the next field in the schema.
        current = current?.[part];

        // If the current node is a nested object (has "properties" but not "semantic_attributes"),
        // continue traversal into its properties.
        if (
          current?.properties &&
          typeof current.properties === 'object' &&
          !current.semantic_attributes
        ) {
          current = current.properties;
        }
      }
    }

    if (!current || typeof current !== 'object') return null;

    // Prefer the new format: "semantic_attributes" object with "semantic_type".
    if (current.semantic_attributes?.semantic_type) {
      return current.semantic_attributes;
    }

    // Fallback: legacy format where semantic info is directly on the node.
    return current.semantic_type ? current : null;
  };

  /**
   * Traverses a schema node that represents an array, handling both modern JSON Schema and legacy formats.
   *
   * General scenario:
   * - In JSON Schema, arrays are described with an "items" property that defines the schema for array elements.
   * - In legacy Nexla schemas, arrays may be represented as a direct array of schemas.
   * - Sometimes, a schema node may have type "array" but lack an "items" property (malformed or incomplete schema).
   *
   * This function:
   * 1. If the schema has an "items" property (standard JSON Schema), it delegates to _traverseSchemaStructure for the items.
   * 2. If the schema is an array (legacy format), it returns the first element as the schema for array items.
   * 3. If the schema is an object with type "array" but no "items", it returns the schema itself (so semantic info can still be extracted).
   * 4. Otherwise, returns null (not an array or unrecognized format).
   */
  _traverseArrayItems = (schema) => {
    if (!schema || typeof schema !== 'object') {
      return null;
    }

    // 1. Standard JSON Schema array: use "items" property
    if (schema.items) {
      return this._traverseSchemaStructure(schema.items);
    }
    // 2. Legacy format: schema is an array of schemas
    else if (Array.isArray(schema)) {
      return schema[0] ?? null;
    }
    // 3. Malformed schema: type is "array" but no "items"
    else if (schema.type === 'array') {
      // Return the schema itself so semantic info can be extracted
      return schema;
    }

    // 4. Not an array or unrecognized format
    return null;
  };

  /**
   * Traverses a JSON schema node and returns the relevant sub-structure for further navigation.
   *
   * This handles the following scenarios:
   * - If the schema uses `anyOf` or `oneOf` (union types), it finds the first object type in the union.
   *   This is common in OpenAPI/JSON Schema where a field can be multiple types (e.g., string or object).
   * - If the schema is a direct object with `properties`, it returns the properties object for further traversal.
   * - If the schema is an object type without explicit properties, it returns the schema itself.
   * - For primitive types or other structures (e.g., string, number, array), it returns the schema itself,
   *   so that semantic information (like semantic_attributes) can be extracted from the type definition.
   */
  _traverseSchemaStructure = (schema) => {
    if (!schema || typeof schema !== 'object') {
      return null;
    }

    // Handle anyOf union types (e.g., field can be one of several types)
    if (schema.anyOf?.length > 0) {
      return this._findObjectTypeInUnion(schema.anyOf);
    }

    // Handle oneOf union types (e.g., field can be exactly one of several types)
    if (schema.oneOf?.length > 0) {
      return this._findObjectTypeInUnion(schema.oneOf);
    }

    // Handle direct object with properties (standard object definition)
    if (schema.properties && typeof schema.properties === 'object') {
      return schema.properties;
    }

    // Handle object type without explicit properties (may be a placeholder for an object)
    if (schema.type === 'object') {
      return schema;
    }

    // For primitive types or other structures, return the schema itself
    // This allows semantic info to be extracted from the type definition
    return schema;
  };

  /**
   * Given a list of union types (e.g., from anyOf/oneOf in JSON Schema),
   * this function attempts to find and return the most relevant object type for further schema traversal.
   *
   * The logic is as follows:
   * 1. If the unionTypes array is empty or not an array, return null.
   * 2. Search for the first item in the union that is an object with explicit properties.
   *    - If found, return its properties object (for further schema navigation).
   * 3. If no such object is found, search for any item that is an object type (even if it has no properties).
   *    - If found, return the object type itself.
   * 4. If neither is found, return null.
   *
   * This is used to handle cases where a schema field can be multiple types,
   * and we want to drill into the object structure if available.
   */
  _findObjectTypeInUnion = (unionTypes) => {
    // Return null if input is not a non-empty array
    if (!Array.isArray(unionTypes) || unionTypes.length === 0) {
      return null;
    }

    // Step 1: Look for the first object type with explicit properties
    const objectType = unionTypes.find(
      (item) => item?.properties && typeof item.properties === 'object',
    );

    if (objectType) {
      // Return the properties object for further traversal
      return objectType.properties;
    }

    // Step 2: If no object with properties, look for any object type
    const anyObjectType = unionTypes.find((item) => item?.type === 'object');

    // Return the object type itself, or null if not found
    return anyObjectType ?? null;
  };

  getParentHierarchy = (row, schema, attrNameToIndexMap) => {
    const parents = [];
    let parentName = row?.__parentName;

    while (parentName) {
      parents.push(parentName);
      parentName = schema[attrNameToIndexMap[parentName]]?.__parentName;
    }

    return parents;
  };

  _showSearchResults = (schema, searchText) => {
    const { attrNameToIsExpandedMap: oldAttrNameToIsExpandedMap, attrNameToIndexMap } = this.state;
    const attrNameToIsExpandedMap = { ...oldAttrNameToIsExpandedMap };
    const attrNameToContainsSearchMatchMap = {};

    /**
     * Get all rows that match the search text
     */
    const matchedRows = schema.filter((row) => this._rowMatchesSearchText(row, searchText));

    /**
     * For each row, get its parent hierarchy
     */
    matchedRows.forEach((row) => {
      const parentHierarchy = this.getParentHierarchy(row, schema, attrNameToIndexMap);

      parentHierarchy.forEach((parentName) => {
        attrNameToIsExpandedMap[parentName] = true;
        attrNameToContainsSearchMatchMap[parentName] = true;
      });
    });

    const filteredSchema = this._getFilteredSchema(
      schema,
      searchText,
      attrNameToIndexMap,
      attrNameToIsExpandedMap,
      attrNameToContainsSearchMatchMap,
    );

    this.setState({
      filteredSchema,
      attrNameToContainsSearchMatchMap,
      attrNameToIsExpandedMap,
    });
  };

  _rowMatchesSearchText = (row, searchText) => {
    if (!searchText || searchText.length === 0) return true;

    const lowerCaseSearchText = searchText.toLowerCase();

    return (
      row.__name.toLowerCase().indexOf(lowerCaseSearchText) > -1 ||
      (row.__description && row.__description.toLowerCase().indexOf(lowerCaseSearchText) > -1)
    );
  };

  _getEffectiveSchema = (props) => {
    const { schema, metaSchema, showMeta } = props;

    return showMeta ? [...(metaSchema || []), ...(schema || [])] : schema;
  };

  _getEffectiveSamples = () => {
    const { samples, showMeta, selectedSplitOutputId } = this.props;
    const splitOutputSamples = selectedSplitOutputId
      ? filter(samples, ({ forkToDataset }) => forkToDataset == selectedSplitOutputId)
      : samples;

    return showMeta
      ? map(splitOutputSamples, (sample) => ({
          ...sample,
          attrs: { ...sample.meta, ...sample.attrs },
        }))
      : splitOutputSamples;
  };

  _getDatum = (index) => {
    const { filteredSchema } = this.state;
    return filteredSchema[index % filteredSchema.length];
  };

  _getColumnAttrs = (columnIndex) => {
    const effectiveColumnIndex = columnIndex - 2;
    const effectiveSamples = this._getEffectiveSamples();

    return effectiveSamples
      ? effectiveSamples[effectiveColumnIndex] && effectiveSamples[effectiveColumnIndex].attrs
      : undefined;
  };

  _getRowId = (index) => {
    const rowData = this._getDatum(index);
    return rowData && rowData.__name;
  };

  _getTransformErrorMessage(transformError) {
    const errorMessage = [transformError.message];

    if (transformError.detail && transformError.detail.length > 0 && transformError.detail[0]) {
      forEach(compact(transformError.detail[0].split(',')), (detailPart, index) => {
        errorMessage.push(<br key={index} />, detailPart);
      });
    }

    return errorMessage;
  }

  _handleInfoClicked = (rowIndex) => {
    this.setState({ infoRowIndex: rowIndex }, () => {
      if (this._grid) {
        this._grid.recomputeGridSize();
      }
    });
  };

  _handleCloseInfo = () => {
    const { onCloseInfo } = this.props;

    this.setState({ infoRowIndex: -1 }, () => {
      if (this._grid) {
        this._grid.recomputeGridSize();
      }

      if (onCloseInfo) {
        onCloseInfo();
      }
    });
  };

  _handleRowSelected = (attrName, isChecked) => {
    const { gridId, selectRecipeBuilderAttr, deselectRecipeBuilderAttr } = this.props;

    if (isChecked) {
      selectRecipeBuilderAttr(attrName, gridId);
    } else {
      deselectRecipeBuilderAttr(attrName, gridId);
    }
  };

  _handleExpandCollapseButtonClick = (event) => {
    event.stopPropagation();
    event.preventDefault();

    const { searchText } = this.props;
    const {
      attrNameToIndexMap,
      attrNameToIsExpandedMap: currentAttrNameToIsExpandedMap,
      attrNameToContainsSearchMatchMap,
    } = this.state;
    const { rowIndex } = event.currentTarget.dataset;
    const rowData = this._getDatum(rowIndex);
    const isExpanded = currentAttrNameToIsExpandedMap[rowData.__name] || false;
    const attrNameToIsExpandedMap = {
      ...currentAttrNameToIsExpandedMap,
      [rowData.__name]: !isExpanded,
    };
    const effectiveSchema = this._getEffectiveSchema(this.props);
    const filteredSchema = this._getFilteredSchema(
      effectiveSchema,
      searchText,
      attrNameToIndexMap,
      attrNameToIsExpandedMap,
      attrNameToContainsSearchMatchMap,
    );

    this.setState(
      {
        attrNameToIsExpandedMap,
        filteredSchema,
      },
      () => {
        if (this._grid) {
          this._grid.recomputeGridSize({ rowIndex });
        }
      },
    );
  };

  _handleShowMetaChanged = () => {
    const { onShowMetaChanged, showMeta } = this.props;
    if (onShowMetaChanged) {
      onShowMetaChanged(!showMeta);
    }
  };
}

RecipeBuilderGrid.defaultProps = {
  selectedViewMode: SchemaEditorViewModeType.TABLE,
  showSamples: true,
  showAttributeInfo: true,
  allowSelection: false,
};

const mapStateToProps = (state, ownProps) => {
  const { gridId } = ownProps;
  const {
    recipeBuilder: { recipeGroups, selectedAttrs },
  } = state;

  return {
    recipeGroups,
    selectedAttrs: selectedAttrs[gridId],
  };
};

export default connect(mapStateToProps, {
  selectRecipeBuilderAttr,
  deselectRecipeBuilderAttr,
})(RecipeBuilderGrid);
